import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Select, Spin, message, Empty } from 'antd';
import type { SelectProps } from 'antd';
import './SuperAdminBanner.css';
import CustomModalWithoutButtons from '@/components/CustomModalWithoutButtons';
import CustomButton from '@/components/CustomButton';
import { transferOwnership } from '@/apis/settings';
import { CustomTitle } from '@/components';
import { fetchMentionUsers } from '@/apis/shipment';
import { useToken } from '@/common/helper';
import { persistor, resetDashboardFilterStore } from '@/redux-toolkit/store';
import { useWebSocket } from '@/context/WebSocketContext';
import { useNavigate } from 'react-router-dom';
import { useSpin } from '@/components/SpinContext';

interface Person {
  id: string;
  name: string;
  email: string;
  initials?: string;
  badge_color?: string;
}

interface TransferOwnershipModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (person: Person) => void;
}

const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({
  open,
  onClose,
  onContinue,
}) => {
  const { token, decodedUserToken, removeToken, setUserId } = useToken();
  const { webSocketService, setSelectedUserID } = useWebSocket() || {};
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [optionsFromApi, setOptionsFromApi] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { showSpin, hideSpin } = useSpin();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (open) {
      setSelectedId(undefined);
      setSearch('');
    }
  }, [open]);

  const debounceRef = useRef<number | undefined>(undefined);
  const debounce = (fn: () => void, delay = 150) => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(fn, delay);
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchMentionUsers(token ?? '', '')
      .then((res) => {
        const data = res?.data?.data?.data || [];
        const formatted: Person[] = data
          .filter(
            (item) => String(item?.id) !== String(decodedUserToken()?.user_id)
          )
          .map((item) => ({
            id: String(item?.id),
            name: item?.name,
            email: item?.email,
            initials: item?.badge,
            badge_color: item?.badge_color,
          }));
        setOptionsFromApi(formatted);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, token, decodedUserToken]);

  const handleSearch = (q: string) => {
    debounce(() => setSearch(q || ''));
  };

  const filteredPeople = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return optionsFromApi;
    return optionsFromApi.filter((p) => {
      const name = p.name?.toLowerCase() || '';
      const email = p.email?.toLowerCase() || '';
      const ini = p.initials?.toLowerCase?.() || '';
      return name.includes(q) || email.includes(q) || ini.includes(q);
    });
  }, [optionsFromApi, search]);

  const options = useMemo(
    () =>
      filteredPeople.map((p) => ({
        value: p.id,
        label: (
          <div className='person-option'>
            <div
              className='person-avatar'
              aria-hidden
              // eslint-disable-next-line react/forbid-dom-props -- the badge colour comes from the user data
              style={{ backgroundColor: p.badge_color || 'var(--gray-300)' }}
            >
              {(p.initials && p.initials.trim()) ||
                p.name
                  .split(' ')
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join('')}
            </div>
            <div className='person-meta'>
              <div className='person-name'>{p.name}</div>
              <div className='person-email'>{p.email}</div>
            </div>
          </div>
        ),
        name: p.name,
        email: p.email,
        raw: p,
      })),
    [filteredPeople]
  );

  const selected = useMemo(
    () => optionsFromApi.find((p) => p.id === selectedId) || null,
    [optionsFromApi, selectedId]
  );

  const tagRender: NonNullable<SelectProps['tagRender']> = (tagProps) => {
    const person = optionsFromApi.find((p) => p.id === tagProps.value);
    if (!person) return <></>;
    const onPrevent = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    return (
      <span className='own-chip' onMouseDown={onPrevent}>
        <span className='own-chip-label'>{person.name}</span>
        <span className='own-chip-x' onClick={tagProps.onClose}>
          ×
        </span>
      </span>
    );
  };
  const logoutAndReset = () => {
    setTimeout(() => {
      removeToken();
      setUserId(null);
      setSelectedUserID?.(null);

      persistor
        .flush()
        .then(() => persistor.purge())
        .then(() => {
          resetDashboardFilterStore();
          localStorage.clear();
          return navigate('/login');
        })
        .catch(console.error);

      webSocketService?.disconnect?.();
    }, 3000);
  };
  const handleContinue = () => {
    if (!selectedId || !selected) return;
    showSpin();
    setSubmitting(true);
    transferOwnership(token ?? '', { user_id: String(selectedId) })
      .then((res) => {
        const data = res?.data?.data || null;
        if (data?.is_success) {
          message.success(data?.message);
          onContinue(selected);
          logoutAndReset();
          onClose();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        hideSpin();
        setSubmitting(false);
      });
  };

  return (
    <CustomModalWithoutButtons
      modalOpen={open}
      setModalOpen={(next) => {
        if (!next) onClose();
      }}
      modalTitle=''
      modalSize={500}
      modalBorderRadius='12px'
      closeIconSize='10px'
    >
      <CustomTitle
        text='Transfer Zoomlogi account ownership'
        fontSize={20}
        fontWeight='700'
        lineHeight='28px'
        color='var(--text-primary)'
        marginBottom={0}
      />

      <Flex vertical gap={16}>
        <Select
          className='select-ownership'
          value={selectedId ? [selectedId] : []}
          onChange={(vals: string[]) => {
            setSelectedId(vals.slice(-1)[0]);
            setDropdownVisible(false);
          }}
          placeholder='Enter name...'
          style={{ width: 320 }}
          options={options}
          showSearch
          filterOption={false}
          onSearch={handleSearch}
          notFoundContent={
            loading ? (
              <Spin size='small' />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description='No matches'
              />
            )
          }
          suffixIcon={null}
          bordered={false}
          mode='multiple'
          tagRender={tagRender}
          open={dropdownVisible}
          onDropdownVisibleChange={setDropdownVisible}
        />
        <Flex justify='end' gap={12}>
          <CustomButton
            key='cancel'
            text='Cancel'
            onClick={onClose}
            bgColor='transparent'
            color='var(--text-primary)'
            border='none'
            boxShadow='none'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            padding='15px 15px 15px 0px'
            borderRadius='8px'
          />
          <CustomButton
            key='save'
            text={submitting ? 'Continuing...' : 'Continue'}
            bgColor='#000000'
            color='#FFFFFF'
            fontSize='14px'
            width='110px'
            borderRadius='8px'
            fontWeight='600'
            height='36px'
            onClick={handleContinue}
            isFormValid={!selected || submitting}
          />
        </Flex>
      </Flex>
    </CustomModalWithoutButtons>
  );
};

export default TransferOwnershipModal;
